import { lazy, Suspense, useState } from 'react'
import {
  NavLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import {
  useClubAdminGetBillingStatusQuery,
  useClubAdminGetTemplateQuery,
  useGetClubDetailsQuery,
} from '../../../features/SpeechAgendaApiSlice.ts'
import { hasCustomTemplate } from '../../../components/clubAdmin/hasCustomTemplate.ts'
import { useSetTitle } from '../../../app/hooks/UseSetTitle.ts'
import { useAppAuth } from '../../../app/hooks/UseAppAuth.ts'
import ClubMembers from '../../../components/clubAdmin/ClubMembers.tsx'
import ClubMeetingsAdmin from '../../../components/clubAdmin/ClubMeetingsAdmin.tsx'
import ClubBilling from '../../../components/clubAdmin/ClubBilling.tsx'
import DigestSettings from '../../../components/clubAdmin/DigestSettings.tsx'
import DigestOccurrences from '../../../components/clubAdmin/DigestOccurrences.tsx'
// Lazy-loaded: the rich email editor is a heavy dependency, so its chunk should
// only load when the Meeting Digest tab is opened.
const EmailTemplateEditor = lazy(
  () => import('../../../components/clubAdmin/EmailTemplateEditor.tsx')
)
import OnboardingChecklist from '../../../components/clubAdmin/OnboardingChecklist.tsx'
import LoadingSpinner from '../../../components/icons/LoadingSpinner.tsx'
import Error404 from '../../Error404.tsx'

const tabClass = ({ selected }: { selected: boolean }) =>
  [
    'shrink-0 whitespace-nowrap px-1 pb-3 text-sm font-medium border-b-2 focus:outline-none',
    selected
      ? 'border-indigo-600 text-indigo-600'
      : 'border-transparent text-gray-500 hover:text-gray-700',
  ].join(' ')

const ClubAdminPanelPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { clubId } = useParams()
  const { userLoaded } = useAppAuth()

  const fromMeeting = (location.state as { fromMeeting?: string } | null)
    ?.fromMeeting

  // Controlled so the onboarding checklist can jump to the relevant tab.
  const [selectedTab, setSelectedTab] = useState(0)

  const {
    data: clubDetails,
    isLoading,
    isError,
  } = useGetClubDetailsQuery(clubId ?? '', {
    skip: !clubId || !userLoaded,
  })

  const isAdmin = clubDetails?.roleId === 1

  // Billing is an admin-only endpoint, so hold off until the role guard clears.
  const { data: billing, isLoading: isBillingLoading } =
    useClubAdminGetBillingStatusQuery(clubId ?? '', {
      skip: !clubId || !userLoaded || !isAdmin,
    })

  const showBilling = !billing?.billingExempt

  // Gated here rather than inside the editor so the heavy lazy chunk never loads for
  // clubs that can't see it. The editor issues the same query and hits this cache entry.
  // currentData, not data: this route stays mounted across club switches, and `data`
  // would keep the previous club's response — briefly revealing the editor for a club
  // that has no template. isError on top of that, because currentData also survives a
  // failed refetch of the same club; without it the section would render its inline
  // error card, and the rule is that a failed load shows nothing at all.
  const { currentData: template, isError: isTemplateError } =
    useClubAdminGetTemplateQuery(clubId ?? '', {
      skip: !clubId || !userLoaded || !isAdmin,
    })

  const [searchParams] = useSearchParams()
  // TODO: confirm the ?customTemplate=1 escape hatch is how we want to opt a new club in,
  // rather than enabling it directly in the database.
  const showTemplateEditor =
    (!isTemplateError && hasCustomTemplate(template)) ||
    searchParams.get('customTemplate') === '1'

  // Gate the spinner on the initial load only. Background refetches (triggered
  // by any admin mutation invalidating 'clubDetails') keep `clubDetails`
  // populated, so blanking the whole panel on `isFetching` would unmount the
  // child sections — and any open confirmation modal — every time you save or
  // delete something.
  const isPending = isLoading || isBillingLoading || !userLoaded

  const clubName = clubDetails?.clubName

  useSetTitle(clubName ? `${clubName} Admin` : 'Club Admin')

  if (isPending) {
    return (
      <div className="flex justify-center mt-10">
        <LoadingSpinner message="Loading club admin data..." />
      </div>
    )
  }

  if (!clubDetails || isError || !isAdmin) {
    return <Error404 />
  }

  return (
    <section className="relative max-w-5xl mx-auto">
      {fromMeeting && (
        <NavLink
          to={`/club/${clubId}/meeting/${fromMeeting}`}
          className="md:absolute md:-top-[79px] md:right-0 -mt-[15px] mb-[25px] md:mt-0 md:mb-0 inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
          Back to meeting admin
        </NavLink>
      )}
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Club Admin
        </h1>
        <p className="text-sm text-gray-600">
          Manage schedule, membership and roles for {clubDetails.clubName}
        </p>
      </header>

      {/* Keyed on the club: the checklist latches once whether the club was already set up, and this
          route stays mounted when clubId changes, so the latch must not outlive the club */}
      <OnboardingChecklist
        key={clubId}
        clubId={clubId ?? ''}
        onSelectTab={setSelectedTab}
      />

      <TabGroup selectedIndex={selectedTab} onChange={setSelectedTab}>
        {/* The line under the tabs is an inset shadow, not a border: the row scrolls sideways on a
            narrow screen, and a scrolling box would clip a tab underline pulled over its border */}
        <TabList className="flex gap-6 overflow-x-auto mb-6 shadow-[inset_0_-1px_0_theme(colors.gray.200)]">
          <Tab className={tabClass}>Schedule Management</Tab>
          <Tab className={tabClass}>Member Management</Tab>
          <Tab className={tabClass}>Meeting Digest</Tab>
          {showBilling && <Tab className={tabClass}>Billing</Tab>}
        </TabList>
        <TabPanels>
          <TabPanel>
            <ClubMeetingsAdmin />
          </TabPanel>
          <TabPanel>
            <ClubMembers />
          </TabPanel>
          <TabPanel>
            <DigestSettings clubId={clubId ?? ''} />
            <DigestOccurrences clubId={clubId ?? ''} />
            {showTemplateEditor && (
              <Suspense
                fallback={
                  <div className="mt-12 flex justify-center">
                    <LoadingSpinner message="Loading email editor..." />
                  </div>
                }>
                <EmailTemplateEditor clubId={clubId ?? ''} />
              </Suspense>
            )}
          </TabPanel>
          {showBilling && (
            <TabPanel>
              <ClubBilling clubId={clubId ?? ''} />
            </TabPanel>
          )}
        </TabPanels>
      </TabGroup>

      <footer className="flex justify-center mt-8">
        <button
          type="button"
          onClick={() => navigate(`/club/${clubId}`)}
          className="block rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          View club page
        </button>
      </footer>
    </section>
  )
}

export default ClubAdminPanelPage
